import { createRouter, createWebHistory } from "vue-router";
import { useCache } from "@misa/ui-base";
import auth from "@/commons/authService";
import commonFn from "@/commons/commonFunction";
import popupUtil from "@/commons/popupUtil";
import i18n from "@/i18ns/i18n";
import i18nEnum from "@/i18ns/vi/i18nEnum";
import resourceAPI from "@/apis/system/ResourceAPI";
import { RouteName } from "@/routers/routeNames";
import { CacheKeys } from "@/constants";
import { useAuthStore } from "@/stores/AuthStore";
import { useLoadingStore } from "@/stores/LoadingStore";

const { getCache } = useCache();
const ROLES = i18nEnum.RoleCategoryMerchant;

let _authStoreInited = false;
let routeEmbedPaymentDemo = [];

if (commonFn.getLocalStorage("CurrentMerchant")) {
  let merchantCode = JSON.parse(commonFn.getLocalStorage("CurrentMerchant")).merchant.code;

  if (
    window.embedPaymentDemo.enable &&
    (window.embedPaymentDemo.pilotAllMerchant ||
      window.embedPaymentDemo.merchantPilot.includes(merchantCode))
  ) {
    routeEmbedPaymentDemo = [
      {
        path: "/embedpaymentdemo/marketing",
        name: "EmbedPaymentDemoMarketing",
        component: () => import("@/views/EmbedPaymentDemo/MarketingDemo.vue"),
      },
      {
        path: "/embedpaymentdemo/marketing/monitoraction/list",
        name: "EmbedPaymentDemoMarketingMonitorActionList",
        component: () => import("@/views/EmbedPaymentDemo/MarketingMonitorActionList.vue"),
      },
      {
        path: "/embedpaymentdemo/tuition",
        name: "EmbedPaymentDemoTuition",
        component: () => import("@/views/EmbedPaymentDemo/EmbedPaymentTuition.vue"),
      },
    ];
  }
}

const router = createRouter({
  history: createWebHistory(window?._basePath ?? ""),
  scrollBehavior() {
    return { left: 0, top: 0 };
  },
  routes: [
    {
      path: "/",
      component: () => import("@/views/login/MisaID.vue"),
      meta: { anonymous: true },
    },
    {
      path: "/",
      component: () => import("@/layouts/MainLayout.vue"),
      children: [
        {
          path: "/memas",
          name: RouteName.Memas,
          component: () => import("@/views/SignUpPaymentGateways/memasIdentify/inforUnit.vue"),
          meta: { anonymous: true },
        },
        {
          path: "/dashboard/:type",
          name: RouteName.DashboardOfficial,
          component: () => import("@/views/dashboard/Dashboard.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin] },
        },
        {
          path: "/dashboard",
          name: RouteName.Dashboard,
          component: () => import("@/views/dashboard/Dashboard.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin] },
        },
        {
          path: "/demo",
          name: RouteName.Demo,
          component: () => import("@/views/demo.vue"),
          meta: { anonymous: true },
        },
        {
          path: "/demo2",
          name: RouteName.Demo2,
          component: () => import("@/views/demo2.vue"),
          meta: { anonymous: true },
        },
        {
          path: "/demo3",
          name: RouteName.Demo3,
          component: () => import("@/views/demo3.vue"),
          meta: { anonymous: true },
        },
        {
          path: "/demo4",
          name: RouteName.Demo4,
          component: () => import("@/views/document/TemplateList.vue"),
          meta: { anonymous: true },
        },
        // Danh sách quản lý giao dịch
        {
          path: "/transactions",
          name: RouteName.Transactions,
          component: () => import("@/views/transaction/TransactionList.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin, ROLES.Merchant_TransactionManagement] },
        },
        {
          path: "transaction-details",
          name: RouteName.TransactionDetails,
          component: () => import("@/views/transaction/TransactionDetails.vue"),
        },
        // Transaction details by token
        {
          path: "transactiondetails/:token/:transactionId",
          name: RouteName.TransactionDetailsToken,
          component: () => import("@/views/transaction/TransactionDetailsToken.vue"),
          meta: { anonymous: true },
        },
        // Danh sách nhân viên
        {
          path: "/employee-dictionary",
          name: RouteName.Employee,
          component: () => import("@/views/employee/EmployeeList.vue"),
        },
        // Tài khoản phụ
        {
          path: "/subaccount",
          name: RouteName.SubAccount,
          component: () => import("@/views/SubAccount/SubAccountList.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin] },
        },
        // Quản lý người dùng
        {
          path: "/user-dictionary",
          name: RouteName.User,
          component: () => import("@/views/usersystem/user/UserList.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin] },
        },
        // Vai trò
        {
          path: "/role",
          name: RouteName.Role,
          component: () => import("@/views/usersystem/role/RoleList.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin] },
        },
        // Quản lý khách hàng
        {
          path: "/customer",
          name: RouteName.Customer,
          component: () => import("@/views/customer/CustomerList.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin] },
        },
        // Kết nối
        {
          path: "/connect",
          name: "connect",
          component: () => import("@/views/usersystem/role/RoleList.vue"),
        },
        // Tùy chọn
        {
          path: "/dboption",
          name: "dboption",
          component: () => import("@/views/usersystem/dboption/DBOptionDetail.vue"),
        },
        {
          path: "/auditing-log",
          name: RouteName.AuditingLog,
          component: () => import("@/views/auditinglog/AuditingLog.vue"),
        },
        {
          path: "/audit",
          name: RouteName.Audit,
          component: () => import("@/views/audit/AuditList.vue"),
        },
        // Báo cáo đối soát cho merchant
        {
          path: "/reportcontrol",
          name: RouteName.ReportControl,
          component: () => import("@/views/TransactionControl/ReportControlV2.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin, ROLES.Merchant_ControlManagement] },
        },
        {
          path: "/control-report/view/:id/:tabName",
          name: RouteName.ControlReportDetail,
          component: () => import("@/views/TransactionControl/MerchantControlReportDetailV2.vue"),
        },
        // Hoàn tiền
        {
          path: "/refund",
          name: RouteName.Refund,
          component: () => import("@/views/Refund/refund.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin, ROLES.Merchant_ComplaintAndRefund] },
        },
        {
          path: "refund-details",
          name: RouteName.RefundDetails,
          component: () => import("@/views/Refund/RefundDetails.vue"),
        },
        // Payment Link
        {
          path: "/paymentlink",
          name: RouteName.PaymentLinkList,
          component: () => import("@/views/paymentlink/PaymentLinkList.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin, ROLES.Merchant_Paymentlink] },
        },
        // Hồ sơ merchant
        {
          path: "/profile-info",
          name: RouteName.ProfileInfo,
          component: () => import("@/views/ProfileInfo/ProfileInfo.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin, ROLES.Merchant_ProfileInfo] },
        },
        {
          path: "/profile-info/update-request",
          name: RouteName.ProfileUpdateRequest,
          component: () => import("@/views/ProfileRequestUpdate/ProfileRequestUpdate.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin, ROLES.Merchant_UpdateRequest] },
        },
        // Setting
        {
          path: "/setting/logo",
          name: RouteName.SettingLogo,
          component: () => import("@/views/MemasSetting/LogoSetting.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin, ROLES.Merchant_Setting] },
        },
        {
          path: "/setting/service",
          name: RouteName.SettingService,
          component: () => import("@/views/MemasSetting/ServiceSetting.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin, ROLES.Merchant_Setting] },
        },
        {
          path: "/setting/wordpress",
          name: RouteName.SettingWordpress,
          component: () => import("@/views/MemasSetting/MemasWordpressSetting.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin, ROLES.Merchant_Setting] },
        },
        {
          path: "/setting/info-control",
          name: RouteName.InfoControl,
          component: () => import("@/views/emailForControl/EmailForControl.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin, ROLES.Merchant_Setting] },
        },
        {
          path: "/payment-bill-detail",
          name: RouteName.PaymentBillDetails,
          component: () => import("@/views/SettingMemas/SettingBillDetail.vue"),
        },
        {
          path: "/payment-shop-detail",
          name: RouteName.PaymentShopDetail,
          component: () => import("@/views/SettingMemas/SettingShopDetail.vue"),
        },
        {
          path: "/payment-website-detail",
          name: RouteName.PaymentWebsiteDetail,
          component: () => import("@/views/SettingMemas/SettingWebsiteDetail.vue"),
        },
        {
          path: "/paymentlink/create",
          name: RouteName.PaymentLinkCreate,
          component: () => import("@/views/paymentlink/PaymentLinkCreate.vue"),
          meta: { requireRole: [ROLES.MerchantAdmin, ROLES.Merchant_Paymentlink] },
        },
        {
          path: "/email-confirm",
          name: RouteName.EmailConfirm,
          component: () => import("@/views/dashboard/EmailConfirm.vue"),
        },
        // Danh sách quản lý khoản thu
        {
          path: "/collection-period/revenue-management",
          name: RouteName.CollectionPeriod,
          component: () => import("@/views/revenuemanagement/RevenueManagementList.vue"),
        },
        // Danh bạ khách hàng
        {
          path: "/contacts",
          name: RouteName.Contacts,
          component: () => import("@/views/customercontacts/list/CustomerContactsList.vue"),
        },
        {
          path: "/import-page/:type",
          name: RouteName.ImportPage,
          component: () => import("@/views/import/ImportPage.vue"),
        },
        {
          path: "/import-page2/:type",
          name: RouteName.ImportPage,
          component: () => import("@/views/import/ImportPage copy.vue"),
        },
        // Chính sách bảo vệ dữ liệu cá nhân
        {
          path: "/history-privacypolicy",
          name: RouteName.HistoryPrivacyPolicy,
          component: () => import("@/views/dashboard/HistoryPrivacyPolicy.vue"),
        },
        ...routeEmbedPaymentDemo,
      ],
    },
    // Trang thanh toán
    {
      path: "/paymentlink/order",
      name: RouteName.PaymentLinkOrder,
      component: () => import("@/views/paymentlink/PaymentLinkOrder.vue"),
      meta: { anonymous: true },
    },
    // Redirect từ payment request
    {
      path: "/paymentrequests/:id",
      name: RouteName.PaymentRequests,
      component: () => import("@/views/revenuemanagement/page/RedirectMailPage.vue"),
      meta: { anonymous: true },
    },
    // Trang success
    {
      path: "/:id/success",
      name: RouteName.Success,
      component: () => import("@/pages/Success.vue"),
      meta: { anonymous: true },
    },
    {
      path: "/success",
      name: RouteName.Success,
      component: () => import("@/pages/Success.vue"),
      meta: { anonymous: true },
    },
    // Trang cancel
    {
      path: "/cancel",
      name: RouteName.Cancel,
      component: () => import("@/pages/Cancel.vue"),
      meta: { anonymous: true },
    },
    // Preview file
    {
      path: "/file-preview",
      name: RouteName.FilePreview,
      component: () => import("@/views/FilePreview/msPDFViewer.vue"),
      meta: { anonymous: true },
    },
    // Embed page xác nhận Chính sách bảo vệ dữ liệu cá nhân
    {
      path: "/embed-agreement",
      name: RouteName.EmbedAgreement,
      component: () => import("@/views/embed-agreement/JetPayEmbedAgreement.vue"),
      meta: { anonymous: true },
    },
    {
      path: "/embed-agreement/:sessionId",
      name: RouteName.EmbedAgreementSession,
      component: () => import("@/views/embed-agreement/JetPayEmbedAgreement.vue"),
      props: true,
      meta: { anonymous: true },
    },
    // Trang test SDK JPAgreementSDK (nhúng iframe embed-agreement)
    {
      path: "/jp-agreement-sdk-test",
      name: RouteName.JPAgreementSdkTest,
      component: () => import("@/views/jp-agreement-sdk-test/JPAgreementSdkTest.vue"),
      meta: { anonymous: true },
    },
    // Biểu mẫu đăng ký
    {
      path: "/dangky",
      name: RouteName.DangKy,
      component: () =>
        import("@/views/SignUpPaymentGateways/RegistrationFormByLandingpage/RegistrationFormByLandingpage.vue"),
      meta: { anonymous: true },
    },
    // Login / Auth
    {
      path: "/login/callback/ampl",
      name: RouteName.AmplCallback,
      component: () => import("@/views/login/AmplCallback.vue"),
      meta: { anonymous: true },
    },
    {
      path: "/login",
      name: RouteName.Login,
      component: () => import("@/views/login/MisaID.vue"),
      meta: { anonymous: true },
    },
    {
      path: "/misaid",
      name: RouteName.MisaId,
      component: () => import("@/views/login/MisaID.vue"),
      meta: { anonymous: true },
    },
    {
      path: "/register",
      name: RouteName.Register,
      component: () => import("@/views/login/Register.vue"),
      meta: { anonymous: true },
    },
    {
      path: "/forgot",
      name: RouteName.Forgot,
      component: () => import("@/views/login/Forgot.vue"),
      meta: { anonymous: true },
    },
    {
      path: "/twofactor/:username/:typeTwf",
      name: RouteName.TwoFactor,
      component: () => import("@/views/login/Twofactor.vue"),
      meta: { anonymous: true },
    },
    {
      path: "/confirm/:sendotp/:username",
      name: RouteName.Confirm,
      component: () => import("@/views/login/Confirm"),
      meta: { anonymous: true },
    },
    {
      path: "/active/:username",
      name: RouteName.Active,
      component: () => import("@/views/login/Register/Active"),
      meta: { anonymous: true },
    },
    {
      path: "/register-unit/:username/:serType",
      name: RouteName.RegisterUnit,
      component: () => import("@/views/login/Register/RegisterUnit"),
      meta: { anonymous: true },
    },
    {
      path: "/gettingstarted",
      name: RouteName.GettingStarted,
      component: () =>
        import(/*webpackChunkName:'getting'*/ "@/views/getting/GettingStartedPage.vue"),
    },
    {
      path: "/setup-success/:type",
      name: RouteName.SetupSuccess,
      component: () => import("@/views/login/SetupSuccess.vue"),
      meta: { anonymous: true },
    },
    // Error pages
    {
      path: "/500",
      name: RouteName.Error500,
      component: () => import(/*webpackChunkName:'pages'*/ "@/pages/500.vue"),
      meta: { anonymous: true },
    },
    {
      path: "/403",
      name: RouteName.Error403,
      component: () => import(/*webpackChunkName:'pages'*/ "@/pages/403.vue"),
      meta: { anonymous: true },
    },
    {
      path: "/ExpiredLicense",
      name: RouteName.ExpiredLicense,
      component: () => import("@/pages/ExpiredLicense.vue"),
      meta: { anonymous: true },
    },
    {
      path: "/sync-resource",
      name: RouteName.SyncResource,
      component: () => import(/*webpackChunkName:'pages'*/ "@/pages/SyncResource.vue"),
      meta: { anonymous: true },
    },
    // 404 catch-all
    {
      path: "/:pathMatch(.*)*",
      name: RouteName.NotFound,
      component: () => import(/*webpackChunkName:'pages'*/ "@/pages/404.vue"),
      meta: { anonymous: true },
    },
    // Test page
    {
      path: "/test",
      name: RouteName.Test,
      component: () => import("@/views/SignUpPaymentGateways/CompanyList.vue"),
      meta: { anonymous: true },
    },
  ],
});

let routerOnReady = function () {};

router.isReady().then(routerOnReady);

router.afterEach(() => {
  useLoadingStore().hide();

  // DBHuan: Do hiện tại chưa migrate hết sang giao diện MISA UI Control => Cần reload lại mỗi trang để đảm bảo route đúng UI
  // if (to.path !== from.path) {
  //   window.location.reload();
  // }
});

router.beforeEach(async (to, from, next) => {
  if (!_authStoreInited) {
    await useAuthStore().init();
    _authStoreInited = true;
  }
  const authStore = useAuthStore();

  const cache = getCache();
  let localData = cache.get(CacheKeys.ROLE_LOCAL);
  if (localData && localData.length) {
    let roleCheckData = localData;
    if (
      to.meta.requireRole &&
      !to.meta.requireRole.some((y) => roleCheckData.some((r) => r == y))
    ) {
      if (!to.name.includes("dashboard")) {
        next(false);
        window.location.href = "/403";
        return;
      }

      let isFullRole = commonFn.isMerchantFullRoleCategory();
      if (!isFullRole) {
        next(false);
        window.location.href = "/403";
        return;
      }
    }
  }

  popupUtil.hideAll();

  if (to.meta.anonymous) {
    next();
    return;
  }

  const isAuthen = await auth.isAuthenticated();
  if (!isAuthen) {
    // Nếu chưa có loginProvider (sau logout) → kiểm tra provider lần cuối để điều hướng đúng
    if (authStore.lastLoginProvider === "MC_AMIS") {
      next({ name: RouteName.AmplCallback });
      return;
    }

    next(false);
    auth.logOut();
    return;
  }

  next();
});

const loadedLanguages = {
  vi: true,
};

function loadLanguageAsync(callback) {
  const lz = i18n,
    lang = lz.locale;

  let loaded = loadedLanguages[lang];
  if (loaded) {
    callback();
    return;
  }

  useLoadingStore().show();
  resourceAPI
    .pull(lang)
    .then((res) => {
      let rs = {};
      for (let i in res) {
        rs[i] = JSON.parse(res[i]);
      }

      loadedLanguages[lang] = true;
      lz.mergeLocaleMessage(lang, rs);
    })
    .catch((err) => {
      console.error(err);
    })
    .finally(() => {
      useLoadingStore().hide();
      callback();
    });
}

export default router;
